/* eslint-disable @typescript-eslint/no-unused-vars */
import "swiper/css/bundle";
import { Fragment, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { BoxCardMobile } from "./BoxCardMobile";
import { boxesBreakpoints } from "./helpers";
import { ReactComponent as BoxesIcon } from "assets/GiftBoxesIcon.svg";
import { BoxCardSkeleton } from "./BoxCardMobile/skeleton";
import { useWebStoreContext } from "contexts/WebStoreProvider";
import { featureTrackerAPI, useGetOffersQuery } from "redux/apis";
import { IShopPurchaseOfferClub } from "contexts/WebStoreProvider/types";
import { orderBy } from "lodash";
import styled from "styled-components";
import { useMediaQuery } from "@mui/material";
import { BannerCard } from "./BannerCard";
import { SectionIconContainer, SectionTitle } from "styles/general_styles";
import { FeatureType } from "interfaces/feature-tracker";
import { ClubGiftsGaloreResponse } from "interfaces/club-gifts-galore-v2";
import { loop } from "utils/loop";
import classnames from "classnames";
import useNewDay from "hooks/useNewDay";

export const SwiperStyled = styled(Swiper)`
	padding-left: 5px;
`;

export const GiftBoxesSection = () => {
	const { store, authToken } = useWebStoreContext();
	const isMobile = useMediaQuery("(max-width: 732px)");
	const { newDay } = useNewDay();
	const {
		data: boxesData,
		isLoading: isBoxesLoading,
		refetch
	} = useGetOffersQuery(
		{
			offerType: "clubGift",
			isAuthenticated: !!authToken?.value
		},
		{ pollingInterval: 600000 }
	);

	const {
		data: galoreData,
		isLoading: isGaloreLoading
	}: { data?: ClubGiftsGaloreResponse; isLoading: boolean } =
		featureTrackerAPI.endpoints.getFeatureTracker.useQueryState({
			FeatureType: FeatureType.CLUB_GIFT_BONUS_V2,
			isAuthenticated: !!authToken?.value
		});

	const orderedOfferes = orderBy(boxesData?.offers, ["priority"], ["asc"]);

	const isSeveralItems = orderedOfferes.find((offer) => {
		return (store?.clubGifts?.find(
			(gift) => gift.id === offer.id && !gift?.timedRewardType
		)?.rewards?.length ?? 0) >= 3
			? true
			: false;
	});

	const hasTimedRewards = orderedOfferes.some((offer) => {
		const gift = store?.clubGifts?.find((g) => g.id === offer.id);
		return !!gift?.timedRewardType;
	});

	useEffect(() => {
		if (galoreData?.tracker?.eventId) {
			refetch();
		}
	}, [galoreData?.tracker?.eventId]);

	useEffect(() => {
		refetch();
	}, [newDay]);

	if (!store || isBoxesLoading || isGaloreLoading) {
		if (!isMobile) {
			return (
				<SwiperStyled breakpoints={boxesBreakpoints}>
					{loop(3, (i: number) => (
						<SwiperSlide key={`box-${i}`}>
							<BoxCardSkeleton />
						</SwiperSlide>
					))}
				</SwiperStyled>
			);
		}

		return (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					width: "auto"
				}}
			>
				{loop(3, (i: number) => (
					<BoxCardSkeleton key={`box-${i}`} />
				))}
			</div>
		);
	}

	return (
		<Fragment>
			{!galoreData?.tracker && boxesData.offers && (
				<BannerCard offer={boxesData?.offers[0]} refetch={refetch} />
			)}
			<SectionTitle id="gift-boxes">
				<SectionIconContainer>
					<BoxesIcon width="21px" height="40px" fill="#073670" />
				</SectionIconContainer>
				<div>GIFT BOXES</div>
			</SectionTitle>

			{isMobile ? (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "center",
						width: "100%"
					}}
				>
					{orderedOfferes.map((offer: IShopPurchaseOfferClub) => (
						<BoxCardMobile
							className={classnames({
								"several-items": isSeveralItems,
								"timed-rewards": hasTimedRewards
							})}
							key={offer.id}
							offer={offer}
							gift={store?.clubGifts?.find((gift) => gift.id === offer.id)}
							eventIsCompleted={galoreData?.tracker?.complete ?? false}
							isFirstPriority={orderedOfferes[0].priority === offer.priority}
						/>
					))}
				</div>
			) : (
				<SwiperStyled breakpoints={boxesBreakpoints} spaceBetween={16}>
					{orderedOfferes.map((offer: IShopPurchaseOfferClub) => (
						<SwiperSlide key={offer.id}>
							<BoxCardMobile
								className={classnames({
									"several-items": isSeveralItems,
									"timed-rewards": hasTimedRewards
								})}
								key={offer.id}
								offer={offer}
								gift={store?.clubGifts?.find((gift) => gift.id === offer.id)}
								eventIsCompleted={galoreData?.tracker?.complete ?? false}
								isFirstPriority={orderedOfferes[0].priority === offer.priority}
							/>
						</SwiperSlide>
					))}
				</SwiperStyled>
			)}
		</Fragment>
	);
};
